import React, { useContext, useState, useEffect, useCallback } from "react";
import { LCard, LDialog, LFade, LComboBox } from "@loffa/gpp-modules";
import { AuthContext } from "../../../../context/AuthContext";
import styles from "./ProductCard.module.css";
import KeyBasedLButton from "../../../Shared/KeyBasedLButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie"; 

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;
const OKTA_CLIENT_ID = process.env.REACT_APP_OKTA_PRODUCT_CLIENT_ID;
const OKTA_DOMAIN = process.env.REACT_APP_OKTA_DOMAIN;
const OKTA_AUTHZ_PATH = process.env.REACT_APP_OKTA_AUTHZ_PATH;
const COOKIE_NAME = "pinnedItems";

const getPinnedClients = () => {
  try {
    return JSON.parse(Cookies.get(COOKIE_NAME) || "{}");
  } catch {
    return {};
  }
};

const compareText = (a, b, numeric = false) => (a || "").localeCompare(b || "", undefined, { numeric, sensitivity: "base" });
const isNumericDtc = (dtc) => /^\d+$/.test(dtc);

// DEV-402: pinned clients first; then numeric DTC#s in number order (0005 < 0745 < 1234); then DTC#s with letters
// (123E, 2SS8, x777) alphabetically; then clients with no DTC#. Ties go by name.
// Pins are read as true/false: "true - undefined" is NaN, which the old sort treated as equal, so pins never came back on reopen.
const sortClients = (clients) => {
  const pinnedClients = getPinnedClients();
  return [...clients].sort((a, b) => {
    const aPinned = Boolean(pinnedClients[a.clientGuid]);
    const bPinned = Boolean(pinnedClients[b.clientGuid]);
    if (aPinned !== bPinned) {
      return aPinned ? -1 : 1;
    }

    const aDtc = (a.dtcNumber || "").trim();
    const bDtc = (b.dtcNumber || "").trim();
    if (!aDtc !== !bDtc) {
      return aDtc ? -1 : 1;
    }

    const aNumeric = isNumericDtc(aDtc);
    if (aNumeric !== isNumericDtc(bDtc)) {
      return aNumeric ? -1 : 1;
    }

    return compareText(aDtc, bDtc, aNumeric) || compareText(a.name, b.name);
  });
};


function ProductCard(props) {
  const context = useContext(AuthContext);
  const [state, setState] = useState({
    displayClientSelection: false,
    clients: [],
    selectedClient: null,
    versionNumber: null,
    versionDate: null
  });
  const [filter, setFilter] = useState("");
  const [filteredClients, setFilteredClients] = useState(state.clients);

  useEffect(() => {
    setFilteredClients(sortClients(state.clients));
  }, [state.clients]);

  const goToProduct = useCallback(() => {
    let url =
      "https://" +
      OKTA_DOMAIN +
      OKTA_AUTHZ_PATH +
      "/v1/authorize" +
      "?client_id=" +
      OKTA_CLIENT_ID +
      "&response_type=code" +
      "&scope=roles+offline_access" +
      "&redirect_uri=" +
      props.productUrl +
      "/verifyauth.aspx" +
      "&state=" +
      btoa(props.productGuid + "|" + state.selectedClient + "|" + props.productTypeId);
    window.location.replace(url);
  }, [props, state.selectedClient]);

  const getVersion = useCallback((accessToken) => {
    let productVersionUrl =
      props.productUrl +
      "/api/Multitenancy/GetProductVersion";

    fetch(productVersionUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      },
    })
      .then((res) => {
        return res.json();
      })
      .then(
        (result) => {
          setState(s =>
          ({
            ...s,
            versionNumber: result.Version,
            versionDate: result.Date
          })
          );
        },
        (error) => {
          setState(s => ({
            ...s,
            versionNumber: 'N/A',
            versionDate: 'N/A',
            error,
          }));
        }
      );
  }, [props]);

  useEffect(() => {
    if (context.authState() != null && typeof (context.authState()) != "undefined" && state.versionNumber == null && props.productGuid != null) {
      getVersion(context.authState().accessToken.accessToken);
    }
  }, [context, getVersion, state.versionNumber, props.productGuid]);

  useEffect(() => {
    if (!state.displayClientSelection && state.selectedClient !== null) {
      goToProduct();
    }
  }, [goToProduct, state.displayClientSelection, state.selectedClient]);

  const checkClients = () => {
    let authContext = context;

    fetch(
      GATEWAY_BASE +
      "/multitenancy/api/users/" +
      authContext.userInfo().loffaUserGuid +
      "/" +
      props.productGuid +
      "/clients",
      {
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        },
      }
    )
      .then((res) => res.json())
      .then(
        (result) => {
          // TODO If multiple, allow selection, else go to product
          if (result.length > 1) {
            setState({
              ...state,
              displayClientSelection: true,
              clients: result,
              selectedClient: null,
            });
            setFilteredClients(result);
          } else if (result.length === 1) {
            setState({
              ...state,
              displayClientSelection: false,
              selectedClient: result[0].clientGuid,
            });
          }
        },
        (error) => {
          setState({
            ...state,
            isLoaded: false,
            error,
          });
        }
      );
  };

  const toggleClientSelection = () => {
    setState({
      ...state,
      displayClientSelection: false,
      clients: [],
      selectedClient: null,
    });
  };

  const selectClient = (e) => {
    setState(s => ({
      ...s,
      selectedClient: e.value ? e.value.clientGuid : null,
    }));
  };

  const handleClientKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const target =
      filteredClients.length === 1
        ? filteredClients[0].clientGuid
        : state.selectedClient;
    if (target) {
      setState(s => ({
        ...s,
        displayClientSelection: false,
        selectedClient: target,
      }));
    }
  };

  const handleFilterChange = (e) => {
    const value = e.filter.value;
    setFilter(value);

    if (value) {
      // DEV-402: ignore case, so "megan", "dtc#0745" and "X777" find their clients
      const search = value.toLowerCase();
      const filteredClientsName = state.clients.filter((client) =>
        (client.formattedName || "").toLowerCase().includes(search)
      );

      setFilteredClients(sortClients(filteredClientsName));
    } else {
      setFilteredClients(sortClients(state.clients));
    }
  };

  let body = (
    <div>
      <div>{props.productDescription}</div>
    </div>
  );

  let header = (
    <div>
      <div className={styles.productIcon}>
        <img className={styles.imgIcon} alt={props.productName + " Product Logo"}
          src={`/assets/productIcons/${props.productLogoFileName
            ? props.productLogoFileName
            : "icon-default.png"
            }`}
        />
      </div>
      <div className={styles.productHeader}>
        <div className={styles.productHeaderName}>
          {props.productName}
        </div>
        <div className={styles.productHeaderSubName}>
          {props.productSubName}
        </div>
        <div className={styles.productHeaderVersion}>
          Version: {state.versionNumber} released on {" "}
          {state.versionDate}
        </div>
      </div>
    </div>
  );

  let selectClientHeader = (
    <div className={styles.flexContainer}>
      <img
        className={`${styles.imgIcon} ${styles.flexContainer}`}
        alt={`${props.productName} Product Logo`}
        src={`/assets/productIcons/${props.productLogoFileName ? props.productLogoFileName : "icon-default.png"}`}
      />
      <div>
        <strong>{props.productName}</strong><br />
        <span className={styles.smallText}>Please select a client</span>
      </div>
    </div>
  );

  const customItemRender = (li, itemProps) => {

    const handleButtonClick = (event) => {
      event.stopPropagation();
      const updatedClientGuid = itemProps.dataItem.clientGuid;

      let pinnedItems = getPinnedClients();
      pinnedItems[updatedClientGuid] = !pinnedItems[updatedClientGuid];
      Cookies.set(COOKIE_NAME, JSON.stringify(pinnedItems), { expires: 365, secure: true, sameSite: "strict" });

      setFilteredClients((prevClients) => sortClients(prevClients));
    }

    const pinnedState = Boolean(getPinnedClients()[itemProps.dataItem.clientGuid]);

    // DEV-436: with a custom itemRender there's no hover text, so show the client's DTC# and name
    return React.cloneElement(
      li,
      li.props,
      <div style={{ display: "flex", width: "100%" }}>
        <div title={itemProps.dataItem.formattedName}>
          {li.props.children}
        </div>      
        <div style={{ marginLeft: "auto" }}>
          <button onClick={ handleButtonClick } style={{ border: "none", padding: "0", fontSize: "10px", transform: pinnedState ? "rotate(90deg)" : "rotate(0deg)" }}>
            <FontAwesomeIcon icon="fa-solid fa-thumbtack" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={styles.productCard}
      style={{
        visibility: props.isVisible === false ? "hidden" : "visible",
      }}
    >
      <LCard
        style={{ width: "425px" }}
        customHeader={header}
        body={body}
        clickAction={checkClients}
      ></LCard>
      <LFade transitionEnterDuration={200}>
        {state.displayClientSelection && (
          <div>
            <LDialog
              key={props.productGuid}
              width="400"
              title={selectClientHeader}
              body={
                <div onKeyDown={handleClientKeyDown}>
                  <LComboBox
                    style={{ width: "100%" }}
                    data={filteredClients}
                    textField="formattedName"
                    itemRender={customItemRender}
                    dataItemKey="clientGuid"
                    placeholder="Select Client"
                    onChange={selectClient}
                    filterable={true}
                    onFilterChange={handleFilterChange}
                  ></LComboBox>
                </div>
              }
              actionLayout="stretched"
              action={
                state.selectedClient ? (
                  <KeyBasedLButton
                    themeColor="primary"
                    className={`green ${styles.stretchButton}`}
                    onClick={goToProduct}
                    parentKey={state.selectedClient}
                    buttonText={"Go"}
                  >
                  </KeyBasedLButton>
                ) : null
              }
              actionCloseButton={toggleClientSelection}
              actionOverlay={toggleClientSelection}
            />
          </div>
        )}
      </LFade>
    </div>
  );
}

export { ProductCard };
